<template>
  <Form
    :action-type="actionType"
    :subtitle="subtitle"
    :disabled="wasDisabled"
    :fallback-link="fallbackLink"
    :form-body="form"
    :show-preloader="isLoadingNow"
    @on-submit="submit"
    @on-edit="setEditMode(true)"
    @on-cancel="setEditMode(false)"
  >
    <template slot="fields">
      <ManualDeadlineModal
        v-if="openedModals.deadline"
        :init-date="manualDeadline"
        :work-shifts="workShifts"
        @set-manually="setManualDeadline"
        @close="openedModals.deadline = false"
      />
      <EntityReferencesModal
        v-if="openedModals.products"
        :preparing-data="form.products_data"
        :table-fields="productsModalFields"
        title="Выбор продуктов проекта"
        link-slug="products"
        not-only-ids
        @choose="setProducts"
        @close="openedModals.products = false"
      />
      <EntityReferencesModal
        v-if="openedModals.user"
        title="Выбор ответственного пользователя"
        link-slug="users"
        :table-fields="userModalFields"
        :additional-filters="userModalFilters"
        :joining-entities="['shifts_calendars']"
        allow-only-one-choise
        not-only-ids
        @choose="(value) => (form.user_id = value)"
        @close="openedModals.user = false"
      />
      <div class="row">
        <div class="col-lg-12">
          <div class="col-lg-6">
            <div
              v-if="formattedDeadline && actionType !== 'read'"
              class="alert"
              role="alert"
              :class="deadlineAlertCss"
            >
              Дата сдачи: {{ formattedDeadline }}
              <div v-if="manualDeadline">
                <span class="alert-link" @click="openModal('deadline')">
                  (изменить
                </span>
                |
                <span class="alert-link" @click="manualDeadline = null">
                  определять автоматически)
                </span>
              </div>
              <div v-else>
                <span class="alert-link" @click="openModal('deadline')">
                  задать вручную
                </span>
                <span
                  v-if="hasSavedAutoDeadline"
                  class="alert-link"
                  @click="eraseSavedAutoDeadline"
                >
                  | сбросить прежнее сгенерированное значение
                </span>
              </div>
            </div>
            <div
              v-if="!formattedDeadline && actionType !== 'read'"
              class="alert alert-info"
              role="alert"
            >
              Чтоб сгенерировать дату сдачи проекта, задайте для всех продуктов
              время изготовления и мин.количество, а также выберите пользователя.
              Или вы, выбрав лишь пользователя,
              <span class="alert-link" @click="openModal('deadline')">
                можете задать её вручную
              </span>
            </div>
          </div>
        </div>
        <div v-if="actionType === 'read'" class="col-lg-12">
          <div class="col-lg-6">
            <div class="alert" role="alert" :class="deadlineAlertCss">
              Дата сдачи: {{ formattedDeadline }}
              <b v-if="deadlineWasReached">(просрочена!)</b>
            </div>
          </div>
        </div>
        <div class="col-lg-12">
          <div
            v-for="(value, key, index) in labels"
            :key="index"
            v-if="value !== 'Продукты'"
            class="col-lg-6 form-group"
          >
            <fieldset class="form-group__fieldset">
              <label>
                {{ value }}
                <span
                  v-if="isRequiredField(validationSchema[key])"
                  class="field__label_required"
                  >*</span
                >
              </label>
              <ForeignKeyInput
                v-if="value === 'Ответственный пользователь'"
                field-name="login"
                :value="form.user_id"
                :record-link="getUserLink"
                :disabled="actionType === 'read'"
                @on-trash-click="removeUser"
                @on-plus-click="openedModals.user = true"
              />
              <select
                v-else-if="value === 'Статус'"
                class="form-control"
                v-model="form.status"
                :disabled="['add', 'read'].includes(actionType)"
              >
                <option
                  v-for="value in selectValues"
                  :key="value"
                  :value="value"
                >
                  {{ value }}
                </option>
              </select>
              <input
                v-else
                class="form-control"
                :name="key"
                placeholder="Отсутствует"
                :maxlength="maxLength[key]"
                :disabled="actionType === 'read'"
                v-model.trim="form[key]"
              />
            </fieldset>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="col-lg-6 form-group">
            <ProjectProductsList
              :list="form.products_data"
              :buttons-are-visible="actionType !== 'read'"
              @on-plus-click="openModal('products')"
              @on-remove="removeProductFromList"
              @on-counts-update="updateCounts"
              @on-deadlines-change="eraseSavedAutoDeadline"
            />
          </div>
        </div>
      </div>
    </template>
  </Form>
</template>

<script>
import { validationMixin } from "vuelidate";
import schemas from "@/stock/validation-schemas";
import { projectService } from "@/_shared/api-services";

import ForeignKeyInput from "@/_shared/components/ui/ForeignKeyInput";
import EntityReferencesModal from "@/_shared/components/modals/EntityReferencesModal";
import Form from "@/_shared/widgets/Form";

import ProjectProductsList from "@/stock/components/ProjectProductsList";
import ManualDeadlineModal from "@/stock/components/ManualDeadlineModal";

import { formsFields } from "@/stock/constants";
import { formats } from "@/_shared/constants";

import { useForm, useNotification } from "@/_shared/mixins";
import { objectHelper } from "@/_shared/utils";
const { createCopy, createFromKeys } = objectHelper;
export default {
  mixins: [validationMixin, useForm, useNotification],
  components: {
    ForeignKeyInput,
    ProjectProductsList,
    ManualDeadlineModal,
    EntityReferencesModal,
    Form,
  },
  data() {
    return {
      openedModals: {
        products: false,
        user: false,
        deadline: false,
      },
      hasSavedAutoDeadline: false,
      manualDeadline: null,
      userModalFilters: [{
        field: 'calendar_id',
        predicate: 'nis',
        value: null,
      }],
      userModalFields: {
        name: 'Имя',
        surname: 'Фамилия',
        login: 'Логин',
      },
      productsModalFields: {
        name: 'Название',
        type: 'Тип',
        gender: 'Пол',
        barcode: 'Штрихкод',
      },
      fallbackLink: "/projects",
      entityName: {
        readonly: "Проект",
        modify: "проекта",
      },
      form: createFromKeys(formsFields.project.labels),
    };
  },
  validations() {
    return {
      form: this.validationSchema,
    };
  },
  async created() {
    const addingFields = createCopy(this.form);
    await this.setFormFields(addingFields, projectService.get).then(() => {
      if (this.form.deadline_type && this.form.deadline_type === 'manual') {
        this.manualDeadline = new Date(this.form.deadline_datetime);
      }
      if (this.form.deadline_type && this.form.deadline_type === 'auto') {
        this.hasSavedAutoDeadline = true;
      }
      if (!this.form.status) {
        this.form.status = "в работе";
      }
    });
  },
  computed: {
    deadlineAlertCss() {
      const alertModifier = this.deadlineWasReached ? 'danger' : 'info';
      return [`alert-${alertModifier}`];
    },
    formattedDeadline() {
      if (this.deadline) {
        return this.deadline.toLocaleString('ru', formats.dates.deadline);
      }
    },
    deadlineWasReached() {
      if (!this.deadline || this.form.status !== 'в работе') {
        return false;
      }
      const deadlineTimestamp = new Date(this.deadline).getTime();
      const currentTimestamp = Date.now();
      return !!(currentTimestamp > deadlineTimestamp);
    },
    deadline() {
      if (this.manualDeadline !== null) {
        return this.manualDeadline;
      }
      const hasProducts = this.form.products_data
        && this.form.products_data.length > 0;
      if (!hasProducts) {
        return null;
      }
      return this.autoDeadline;
    },
    autoDeadline() {
      if (this.hasSavedAutoDeadline) {
        const date = new Date(this.form.deadline_datetime);
        return date;
      }
      let result = new Date();
      this.form.products_data.forEach((product) => {
        if (!product.each_item_deadline || !result || product.total_count < 1
            || !this.form.user_id) {
          return result = null;
        }
        let { each_item_deadline, total_count } = product;
        const hours = result.getHours() + (each_item_deadline.getHours() * total_count);
        const minutes = result.getMinutes() + (each_item_deadline.getMinutes() * total_count);
        result.setHours(hours, minutes);
        result = this.checkAutoDeadline(result);
      });
      return result;
    },
    workShifts() {
      if (!this.form.user_id) {
        return null;
      }
      const { times } = this.form.user_id.calendar_id;
      const parsedTimes = JSON.parse(times);
      return parsedTimes;
    },
    selectValues() {
      return formsFields.project.statusValues;
    },
    maxLength() {
      return formsFields.project.maxlength;
    },
    labels() {
      return formsFields.project.labels;
    },
    validationSchema() {
      return schemas.project;
    },
  },
  methods: {
    checkAutoDeadline(date) {
      const times = JSON.parse(this.form.user_id.calendar_id.times);
      let shiftDayWasFound = !!(times[date.toLocaleDateString('sv')]);
      while (!shiftDayWasFound) {
        date = new Date(date.setDate(date.getDate() + 1));
        if (times[date.toLocaleDateString('sv')]) {
          shiftDayWasFound = true;
        }
      }
      return date;
    },
    removeUser() {
      if (this.formattedDeadline) {
        this.manualDeadline = null;
        this.form.deadline_datetime = '';
        const notification = 'Срок сдачи, ввиду возможной'
          + ' разницы в сменах пользователей, был сброшен';
        this.showWarningNotification(notification);
      }
      this.form.user_id = '';
    },
    openModal(type = 'deadline') {
      if (!this.form.user_id) {
        this.showErrorNotification('Вначале укажите пользователя');
        return;
      }
      this.openedModals[type] = true;
    },
    updateCounts(emittingData = {}) {
      const { id, quantity_data } = emittingData.product;
      const totalCount = quantity_data.reduce((acc, val) => (
        acc += parseInt(val.count, 10)
      ), 0);
      this.form.products_data = this.form.products_data.map((product) => {
        if (product.id === id) {
          product.total_count = totalCount;
        }
        return product;
      });
      if (emittingData.valuesWereChanged) {
        this.eraseSavedAutoDeadline();
      }
    },
    setManualDeadline(value) {
      this.manualDeadline = value;
      this.eraseSavedAutoDeadline();
    },
    getUserLink() {
      if (this.form.user_id) {
        const link = `/users/${this.form.user_id.id}`;
        return link;
      }
    },
    removeProductFromList(value) {
      const filteredList = this.form.products_data.filter((product) => {
        if (product.id !== value.id) {
          return product;
        }
      });
      this.form.products_data = filteredList;
      this.eraseSavedAutoDeadline();
    },
    eraseSavedAutoDeadline() {
      if (this.hasSavedAutoDeadline) {
        const notification = 'Сохраненная дата сдачи,'
          + ' сгенерированная ранее, была стерта';
        this.showWarningNotification(notification);
        this.hasSavedAutoDeadline = false;
      }
    },
    setProducts(values = []) {
      values = values.map((value) => {
        const quantity_data = JSON.parse(value.sizes_data).map((size) => ({
          ...size, count: 0,
        }));
        const modifiedValue = {
          ...value,
          quantity_data,
          each_item_deadline: null,
          total_count: 0,
        };
        return modifiedValue;
      });
      if (this.form.products_data.length < 1) {
        return (this.form.products_data = values);
      }
      this.form.products_data = [...this.form.products_data, ...values];
    },
    async submit() {
      if (!this.deadline) {
        this.showErrorNotification('Установка даты сдачи обязательна');
        return;
      }
      const callback = async () => {
        const deadline = {
          type: this.manualDeadline ? 'manual' : 'auto',
          value: this.deadline,
        };
        return await projectService[this.actionType](this.form, deadline);
      };
      await this.sendForm(callback);
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/stock/pages/ProjectForm.vue